<script setup>
import { ref, computed, onMounted } from 'vue';
import { toast } from 'dashboard/composables/useToast';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CrmDataTable from '../../../crm/components/reports/CrmDataTable.vue';
import AppSmartLoader from '../../../crm/components/AppSmartLoader.vue';
import tiktokPixelsAPI from 'dashboard/api/tiktokPixels';

const records = ref([]);
const isFetching = ref(false);
const isSaving = ref(false);
const showForm = ref(false);
const editingId = ref(null);
const confirmDeleteId = ref(null);

const blankForm = () => ({
  name: '',
  pixel_id: '',
  access_token: '',
  event_source: 'web',
  test_mode: false,
  test_event_code: '',
  enabled: true,
});
const form = ref(blankForm());

const EVENT_SOURCES = [
  { id: 'web', label: 'Web (pixel)' },
  { id: 'offline', label: 'Offline' },
  { id: 'crm', label: 'CRM' },
];

const fetchAll = async () => {
  isFetching.value = true;
  try {
    const { data } = await tiktokPixelsAPI.get();
    records.value = data || [];
  } catch (error) {
    records.value = [];
  } finally {
    isFetching.value = false;
  }
};

onMounted(fetchAll);

const isValid = computed(
  () =>
    form.value.name.trim() &&
    form.value.pixel_id.trim() &&
    (editingId.value || form.value.access_token.trim())
);

const openCreate = () => {
  editingId.value = null;
  form.value = blankForm();
  showForm.value = true;
};

const openEdit = row => {
  editingId.value = row.id;
  form.value = {
    name: row.name || '',
    pixel_id: row.pixel_id || '',
    access_token: '',
    event_source: row.event_source || 'web',
    test_mode: !!row.test_mode,
    test_event_code: row.test_event_code || '',
    enabled: row.enabled !== false,
  };
  showForm.value = true;
};

const onSave = async () => {
  if (!isValid.value) return;
  isSaving.value = true;
  const payload = { tiktok_pixel: { ...form.value } };
  // Não sobrescrever token com vazio ao editar.
  if (editingId.value && !form.value.access_token.trim()) {
    delete payload.tiktok_pixel.access_token;
  }
  try {
    if (editingId.value) {
      await tiktokPixelsAPI.update(editingId.value, payload);
    } else {
      await tiktokPixelsAPI.create(payload);
    }
    showForm.value = false;
    toast.success('Pixel salvo.');
    await fetchAll();
  } catch (error) {
    toast.error('Não foi possível salvar o pixel.');
  } finally {
    isSaving.value = false;
  }
};

const onDelete = async row => {
  if (confirmDeleteId.value === row.id) {
    await tiktokPixelsAPI.delete(row.id);
    confirmDeleteId.value = null;
    fetchAll();
  } else {
    confirmDeleteId.value = row.id;
    setTimeout(() => {
      confirmDeleteId.value = null;
    }, 3000);
  }
};

const tableColumns = [
  { key: 'name', label: 'Nome', align: 'left' },
  { key: 'pixel_id', label: 'Pixel ID', align: 'left' },
  { key: 'event_source', label: 'Origem', align: 'left' },
  { key: 'status', label: 'Status', align: 'left' },
  { key: 'actions', label: '', align: 'right' },
];

const statusLabel = row => {
  if (row.enabled === false) return 'Desativado';
  if (row.last_error) return 'Erro no último envio';
  if (row.last_event_at) return 'OK';
  return 'Sem envios';
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <h1 class="text-xl font-medium text-n-slate-12">Pixels & Eventos</h1>
        <p class="mt-1 text-sm text-n-slate-10">
          Envie conversões server-side pro TikTok (Events API), fechando o loop
          de otimização — igual ao Meta CAPI. Cole o Pixel ID e o token do
          Events API (gerado no Events Manager do TikTok). Depois use em
          automações ("deal ganho → enviar evento").
        </p>
      </div>
      <NextButton
        icon="i-lucide-circle-plus"
        label="Adicionar pixel"
        sm
        class="shrink-0"
        @click="openCreate"
      />
    </div>

    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Nome
          <input
            v-model="form.name"
            type="text"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            placeholder="Ex: Pixel IDP"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Pixel ID
          <input
            v-model="form.pixel_id"
            type="text"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            placeholder="Ex: CABC123..."
          />
        </label>
        <label
          class="flex flex-col gap-1 text-xs text-n-slate-10 md:col-span-2"
        >
          Access Token (Events API)
          <input
            v-model="form.access_token"
            type="password"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            :placeholder="
              editingId
                ? 'Deixe vazio para manter o atual'
                : 'Token do Events API'
            "
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Origem do evento
          <select
            v-model="form.event_source"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option v-for="s in EVENT_SOURCES" :key="s.id" :value="s.id">
              {{ s.label }}
            </option>
          </select>
        </label>
        <label class="flex items-center gap-2 text-xs text-n-slate-11 mt-5">
          <input v-model="form.test_mode" type="checkbox" />
          Modo teste
        </label>
        <label
          v-if="form.test_mode"
          class="flex flex-col gap-1 text-xs text-n-slate-10 md:col-span-2"
        >
          Test Event Code
          <input
            v-model="form.test_event_code"
            type="text"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            placeholder="TEST12345"
          />
        </label>
      </div>
      <div class="flex justify-end gap-2">
        <NextButton label="Cancelar" faded sm @click="showForm = false" />
        <NextButton
          label="Salvar"
          sm
          :is-loading="isSaving"
          :disabled="!isValid"
          @click="onSave"
        />
      </div>
    </div>

    <div
      v-if="isFetching && !records.length"
      class="flex items-center justify-center h-40"
    >
      <AppSmartLoader animation="radar" :size="48" />
    </div>

    <div
      v-else-if="!records.length"
      class="flex flex-col items-center justify-center gap-2 p-8 rounded-xl text-n-slate-11 bg-n-alpha-black2"
    >
      <span class="i-lucide-activity size-8 text-n-slate-8" />
      <span class="text-sm">Nenhum pixel cadastrado.</span>
    </div>

    <CrmDataTable
      v-else
      :columns="tableColumns"
      :rows="records"
      export-filename="tiktok_pixels"
    >
      <template #status="{ row }">
        <span class="text-xs text-n-slate-11">{{ statusLabel(row) }}</span>
      </template>
      <template #actions="{ row }">
        <div class="flex items-center justify-end gap-1">
          <button
            type="button"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded text-n-slate-10 hover:text-woot-500 hover:bg-n-alpha-1/50"
            @click="openEdit(row)"
          >
            <span class="i-lucide-pencil size-3" />
            Editar
          </button>
          <button
            type="button"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors"
            :class="
              confirmDeleteId === row.id
                ? 'text-white bg-red-500'
                : 'text-n-slate-10 hover:text-red-500 hover:bg-red-500/10'
            "
            @click="onDelete(row)"
          >
            <span class="i-lucide-trash-2 size-3" />
            {{ confirmDeleteId === row.id ? 'Confirmar?' : 'Excluir' }}
          </button>
        </div>
      </template>
    </CrmDataTable>
  </div>
</template>
